import { SPEAKER_SAMPLE_RATE } from './speaker-audio'

export interface LiveMicrophoneCapture {
  sampleRate: number
  stop (): Promise<void>
}

// Runs inside the AudioWorklet thread: forward every mono block to the page.
const WORKLET_SOURCE = `
class SpeakerLiveCaptureProcessor extends AudioWorkletProcessor {
  process (inputs) {
    const channel = inputs[0] && inputs[0][0]
    if (channel && channel.length) {
      this.port.postMessage(channel.slice(0))
    }
    return true
  }
}
registerProcessor('speaker-live-capture', SpeakerLiveCaptureProcessor)
`

function createAudioContext (): AudioContext {
  const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext
  if (!AudioContextConstructor) {
    throw new Error('当前浏览器不支持音频采集')
  }
  try {
    return new AudioContextConstructor({ sampleRate: SPEAKER_SAMPLE_RATE })
  } catch (_error) {
    return new AudioContextConstructor()
  }
}

/**
 * Open the microphone and deliver raw mono Float32 blocks (at
 * `capture.sampleRate`) to the callback until stop() is called. Prefers an
 * AudioWorklet; falls back to ScriptProcessorNode where worklets are missing.
 */
export async function startLiveMicrophoneCapture (onSamples: (chunk: Float32Array) => void): Promise<LiveMicrophoneCapture> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('当前浏览器不支持麦克风采集')
  }
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  })
  const context = createAudioContext()
  const source = context.createMediaStreamSource(stream)
  // Keep the graph alive without playing the microphone back to the operator.
  const sink = context.createGain()
  sink.gain.value = 0
  sink.connect(context.destination)

  let workletNode: AudioWorkletNode | null = null
  let scriptNode: ScriptProcessorNode | null = null
  let workletUrl = ''
  if (context.audioWorklet && typeof AudioWorkletNode !== 'undefined') {
    try {
      workletUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }))
      await context.audioWorklet.addModule(workletUrl)
      workletNode = new AudioWorkletNode(context, 'speaker-live-capture', { numberOfInputs: 1, numberOfOutputs: 1, channelCount: 1 })
      workletNode.port.onmessage = event => onSamples(event.data as Float32Array)
      source.connect(workletNode)
      workletNode.connect(sink)
    } catch (_error) {
      workletNode = null
    }
  }
  if (!workletNode) {
    scriptNode = context.createScriptProcessor(4096, 1, 1)
    scriptNode.onaudioprocess = event => onSamples(new Float32Array(event.inputBuffer.getChannelData(0)))
    source.connect(scriptNode)
    scriptNode.connect(sink)
  }
  if (context.state === 'suspended') {
    await context.resume().catch(() => undefined)
  }

  let stopped = false
  return {
    sampleRate: context.sampleRate,
    async stop () {
      if (stopped) {
        return
      }
      stopped = true
      try {
        source.disconnect()
        if (workletNode) {
          workletNode.port.onmessage = null
          workletNode.disconnect()
        }
        if (scriptNode) {
          scriptNode.onaudioprocess = null
          scriptNode.disconnect()
        }
        sink.disconnect()
      } catch (_error) {
        // Nodes may already be detached when the context was closed by the browser.
      }
      stream.getTracks().forEach(track => track.stop())
      if (workletUrl) {
        URL.revokeObjectURL(workletUrl)
      }
      await context.close().catch(() => undefined)
    },
  }
}
